import { Navigate, Route, Routes } from 'react-router';
import { Loader } from '@/shared/ui';
import { lazy, Suspense } from 'react';
import { AuthRouteGuard } from '../guards/AuthRouteGuard';
import { PublicRoutes } from './public';
import { LoggedInRedirectGuard } from '../guards/LoggedInRedirectGuard';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { PermissionGuard } from '../guards/PermissionGuard';
import { FeatureGate } from '../guards/FeatureGate';
import { OrgRoleGuard } from '../guards/OrgRoleGuard';

const SidebarLayout = lazy(() =>
  import('../layouts/SidebarLayout').then((module) => ({ default: module.SidebarLayout }))
);
const Auth = lazy(() => import('@/pages/Auth'));
const Login = lazy(() => import('@/pages/Login'));
const SignupWizard = lazy(() => import('@/pages/Signup'));

const PartstechCallback = lazy(() => import('@/pages/PartstechCallback'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then((module) => ({ default: module.AuthCallback })));

const Dashboard = lazy(() => import('@/pages/Dashboard'));
const StarterEstimate = lazy(() =>
  import('@/pages/StarterEstimate').then((module) => ({ default: module.StarterEstimate }))
);
const RepairOrders = lazy(() => import('@/pages/RepairOrders'));
const CustomersRoutes = lazy(() => import('@/pages/Customers'));
const VehicleDetails = lazy(() =>
  import('@/pages/VehicleDetails').then((module) => ({ default: module.VehicleDetailsPage }))
);
const Vehicles = lazy(() => import('@/pages/Vehicles'));
const Appointments = lazy(() =>
  import('@/pages/Appointments').then((module) => ({ default: module.AppointmentsPage }))
);
const InventoryPage = lazy(() => import('@/pages/inventory').then((module) => ({ default: module.InventoryPage })));
const Invoices = lazy(() => import('@/pages/Invoices').then((module) => ({ default: module.InvoicesPage })));
const CreateInvoice = lazy(() => import('@/pages/Invoices').then((module) => ({ default: module.CreateInvoicePage })));
const InvoiceDetail = lazy(() =>
  import('@/pages/InvoiceDetail').then((module) => ({ default: module.InvoiceDetailPage }))
);
const Reports = lazy(() => import('@/pages/reports'));
const PartsReport = lazy(() =>
  import('@/pages/reports/parts-purchases').then((module) => ({ default: module.PartsReport }))
);
const TireReport = lazy(() =>
  import('@/pages/reports/tire-purchases').then((module) => ({ default: module.TireReport }))
);
const TechEfficiencyReport = lazy(() =>
  import('@/pages/reports/tech-efficiency/TechEfficiencyReportPage').then((module) => ({
    default: module.TechEfficiencyReportPage,
  }))
);
const DiscountsReport = lazy(() =>
  import('@/pages/reports/discounts/DiscountsReportPage').then((module) => ({
    default: module.DiscountsReportPage,
  }))
);
const CouponsReport = lazy(() =>
  import('@/pages/reports/coupons/CouponsReportPage').then((module) => ({
    default: module.CouponsReportPage,
  }))
);
const Messages = lazy(() => import('@/pages/Messages').then((module) => ({ default: module.MessagesPage })));
const TimeClock = lazy(() => import('@/pages/TimeClock/TimeClockPage').then((m) => ({ default: m.TimeClockPage })));
const Settings = lazy(() => import('@/pages/Settings'));
const LaborManagement = lazy(() => import('@/pages/LaborManagement'));
const InspectionRoutes = lazy(() => import('@/pages/inspection-create'));
const Assistant = lazy(() => import('@/pages/Assistant').then((module) => ({ default: module.AssistantPage })));
const Marketing = lazy(() => import('@/pages/marketing'));
const OrganizationSettings = lazy(() =>
  import('@/pages/organization/Settings').then((module) => ({ default: module.OrganizationSettingsPage }))
);
const OrganizationLocations = lazy(() =>
  import('@/pages/organization/Locations').then((module) => ({ default: module.OrganizationLocationsPage }))
);
const OrganizationLocationDetail = lazy(() =>
  import('@/pages/organization/LocationDetail').then((module) => ({ default: module.OrganizationLocationDetailPage }))
);
const OrganizationBilling = lazy(() =>
  import('@/pages/organization/Billing').then((module) => ({ default: module.OrganizationBillingPage }))
);

export const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<AuthRouteGuard />}>
          <Route path="/onboarding/partstech-callback" element={<PartstechCallback />} />

          <Route path="/starter-estimate" element={<StarterEstimate />} />

          <Route element={<SidebarLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="/dashboard" element={<Navigate to="/" />} />
            <Route path="/repair-orders/*" element={<RepairOrders />} />
            <Route path="/customers/*" element={<CustomersRoutes />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/vehicles/:id" element={<VehicleDetails />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route
              path="/inventory"
              element={
                <PermissionGuard permission="inventory.read">
                  <InventoryPage />
                </PermissionGuard>
              }
            />
            <Route
              path="/invoices"
              element={
                <PermissionGuard permission="invoices.read">
                  <Invoices />
                </PermissionGuard>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <PermissionGuard permission="invoices.create">
                  <CreateInvoice />
                </PermissionGuard>
              }
            />
            <Route
              path="/invoices/:invoiceId"
              element={
                <PermissionGuard permission="invoices.read">
                  <InvoiceDetail />
                </PermissionGuard>
              }
            />
            <Route path="/reports" element={<Reports />} />
            <Route
              path="/reports/parts-purchases"
              element={
                <FeatureGate feature="reports">
                  <PartsReport />
                </FeatureGate>
              }
            />
            <Route
              path="/reports/tire-purchases"
              element={
                <FeatureGate feature="reports">
                  <TireReport />
                </FeatureGate>
              }
            />
            <Route
              path="/reports/tech-efficiency"
              element={
                <FeatureGate feature="tech_efficiency_report">
                  <PermissionGuard permission="reports.read">
                    <TechEfficiencyReport />
                  </PermissionGuard>
                </FeatureGate>
              }
            />
            <Route
              path="/reports/discounts"
              element={
                <FeatureGate feature="reports">
                  <PermissionGuard permission="reports.read">
                    <DiscountsReport />
                  </PermissionGuard>
                </FeatureGate>
              }
            />
            <Route
              path="/reports/coupons"
              element={
                <FeatureGate feature="marketing">
                  <PermissionGuard permission="reports.read">
                    <CouponsReport />
                  </PermissionGuard>
                </FeatureGate>
              }
            />
            <Route path="/messages" element={<Messages />} />
            <Route path="/marketing/*" element={<Marketing />} />
            <Route path="/time-clock" element={<TimeClock />} />
            <Route path="/settings/*" element={<Settings />} />
            <Route path="/labor" element={<LaborManagement />} />
            <Route
              path="/assistant"
              element={
                <FeatureGate feature="ai_assistant">
                  <Assistant />
                </FeatureGate>
              }
            />
            <Route path="/dvi/*" element={<InspectionRoutes />} />
            <Route path="/organization" element={<Navigate to="/organization/locations" replace />} />
            <Route
              path="/organization/locations"
              element={
                <OrgRoleGuard roles={['org_owner', 'org_admin']}>
                  <OrganizationLocations />
                </OrgRoleGuard>
              }
            />
            <Route
              path="/organization/locations/:shopId"
              element={
                <OrgRoleGuard roles={['org_owner', 'org_admin']}>
                  <OrganizationLocationDetail />
                </OrgRoleGuard>
              }
            />
            <Route
              path="/organization/settings"
              element={
                <OrgRoleGuard roles={['org_owner', 'org_admin']}>
                  <OrganizationSettings />
                </OrgRoleGuard>
              }
            />
            <Route
              path="/organization/billing"
              element={
                <OrgRoleGuard roles={['org_owner', 'org_admin']}>
                  <OrganizationBilling />
                </OrgRoleGuard>
              }
            />
          </Route>
        </Route>

        <Route element={<LoggedInRedirectGuard />}>
          <Route element={<AuthFormLayout />}>
            <Route path="/login" element={<Login />} />
          </Route>

          <Route path="/signup/*" element={<SignupWizard />} />
          <Route path="/auth" element={<Auth />} />
        </Route>

        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="*" Component={PublicRoutes} />
      </Routes>
    </Suspense>
  );
};
